import React from 'react'
import {
	EuiButton,
	EuiPopover,
	EuiContextMenu,
	EuiContextMenuPanelDescriptor,
} from '@equipmentshare/ds2'

type BulkActionsDropdownProps = {
	selectedCount: number,
	isLoading: boolean,
	hasCompletedItems: boolean,
	hasCancelledItems: boolean,
	hasPendingItems: boolean,
	onCreatePurchaseOrder: () => void,
	onCreateTransfer: () => void,
	isTransferFlagLoading: boolean,
	onCreateFulfillmentOrder: () => void,
	canUserCreateFulfillmentOrder: boolean,
	selectionHasInFlightFO: boolean,
	onBulkCompleteLineItems: () => void,
	onBulkReopenLineItems: () => void,
	onBulkCancelLineItems: () => void,
  canUserEditRequests: boolean,
}

export default function BulkActionsDropdown( {
	selectedCount,
	isLoading,
	hasCompletedItems,
	hasCancelledItems,
	hasPendingItems,
	onCreatePurchaseOrder,
	onCreateTransfer,
	isTransferFlagLoading,
	onCreateFulfillmentOrder,
	canUserCreateFulfillmentOrder,
	selectionHasInFlightFO,
	onBulkCompleteLineItems,
	onBulkReopenLineItems,
	onBulkCancelLineItems,
	canUserEditRequests
}: BulkActionsDropdownProps ) {
	const [ isPopoverOpen, setIsPopoverOpen ] = React.useState( false )

	const togglePopover = () => setIsPopoverOpen( ( prev ) => !prev )
	const closePopover = () => setIsPopoverOpen( false )

	// Close popover when loading starts
	React.useEffect( () => {
		if ( isLoading ) {
			closePopover()
		}
	}, [ isLoading ] )

	const menuItems = [
		{
			name: 'Create Purchase Order',
			icon: 'document',
			isAvailable: canUserEditRequests,
			onClick: () => {
				closePopover()
				onCreatePurchaseOrder()
			},
		},
		{
			name: 'Create Transfer',
			icon: 'sortRight',
			// The TO is a stop-gap until the FO path is fully deployed. Hide it for users who
			// can create a fulfillment order so we don't offer both paths at once.
			isAvailable: canUserEditRequests && !canUserCreateFulfillmentOrder,
			disabled: isTransferFlagLoading,
			onClick: () => {
				closePopover()
				onCreateTransfer()
			},
		},
		{
			name: 'Create Fulfillment Order',
			icon: 'package',
			isAvailable: canUserCreateFulfillmentOrder,
			// Item-master readiness is no longer gated here — the user may open the
			// order to substitute an item that does have a master. The Create
			// Fulfillment Order modal flags and blocks any item still missing one.
			disabled: selectionHasInFlightFO,
			toolTipContent: selectionHasInFlightFO
				? 'A fulfillment order is already in flight for one or more selected items.'
				: undefined,
			onClick: () => {
				closePopover()
				onCreateFulfillmentOrder()
			},
		},
		{
			name: 'Mark completed',
			icon: 'check',
			isAvailable: canUserEditRequests,
			disabled: !hasPendingItems,
			onClick: () => {
				closePopover()
				onBulkCompleteLineItems()
			},
		},
		{
			name: 'Reopen',
			icon: 'refresh',
			isAvailable: canUserEditRequests,
			disabled: !hasCompletedItems && !hasCancelledItems,
			onClick: () => {
				closePopover()
				onBulkReopenLineItems()
			},
		},
		{
			name: 'Cancel',
			icon: 'cross',
			isAvailable: true,
			disabled: !hasPendingItems,
			onClick: () => {
				closePopover()
				onBulkCancelLineItems()
			},
		},
	]

	const panels: EuiContextMenuPanelDescriptor[] = [
		{
			id: 0,
			items: menuItems
				.flatMap( ( { isAvailable, ...item } ) => ( isAvailable ? [ item ] : [] ) ),
		},
	]

	// Only show button when items are selected
	if ( selectedCount === 0 ) {
		return null
	}

	const button = (
		<EuiButton
			fill
			iconType="arrowDown"
			iconSide="right"
			onClick={togglePopover}
			isLoading={isLoading}
			disabled={isLoading}
		>
			Actions ({selectedCount})
		</EuiButton>
	)

	return (
		<EuiPopover
			button={button}
			isOpen={isPopoverOpen}
			closePopover={closePopover}
			panelPaddingSize="none"
			anchorPosition="downLeft"
		>
			<EuiContextMenu initialPanelId={0} panels={panels} />
		</EuiPopover>
	)
}
